import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Heading from 'components/atoms/Heading/Heading';
import LoadingIndicator from 'components/atoms/LoadingIndicator/LoadingIndicator';
import Txt from 'components/atoms/Txt/Txt';
import SurveyCard from 'components/molecules/SurveyCard/SurveyCard';
import { DashboardToolbarOptions } from 'components/organisms/DashboardToolbar/DashboardToolbar';
import useRefreshInFocus from 'hooks/useRefreshInFocus';
import React from 'react';
import { useSurveyListFragmentQuery_surveysRef$key } from './__generated__/useSurveyListFragmentQuery_surveysRef.graphql';
import classes from './SurveyList.module.scss';
import { useSurveyListFragmentQuery } from './useSurveyListFragmentQuery';

export type Props = {
  className?: string;
  surveyFilter?: DashboardToolbarOptions;
  onFiltersChanged?: (filter: DashboardToolbarOptions) => void;
  surveysRef: useSurveyListFragmentQuery_surveysRef$key;
  disabled?: boolean;
};

export default function SurveyList({
  surveyFilter = DashboardToolbarOptions.AllSurveys,
  onFiltersChanged,
  surveysRef,
  disabled,
}: Props) {
  const [data, refetch] = useSurveyListFragmentQuery(surveysRef);
  const handleRefetch = React.useCallback(() => {
    refetch({}, { fetchPolicy: 'store-and-network' });
  }, [refetch]);

  useRefreshInFocus(handleRefetch, 60 * 1000);
  const unfilteredSurveys = data.surveys!.edges;
  const surveys = unfilteredSurveys?.filter(
    ({
      node,
    }: {
      readonly node: {
        readonly tags: readonly string[];
        readonly status: string;
        readonly url: string | null;
      };
    }) => {
      if (node.status !== 'AVAILABLE' && !node.url) {
        return false;
      }
      if (surveyFilter === DashboardToolbarOptions.TopSurveys) {
        return node.tags.includes('hot');
      } else if (surveyFilter === DashboardToolbarOptions.OngoingSurveys) {
        return node.status === 'PENDING';
      } else {
        return true;
      }
    },
  );

  function handleShowAll() {
    if (onFiltersChanged) {
      onFiltersChanged(DashboardToolbarOptions.AllSurveys);
    }
  }

  function renderSurveys() {
    if (surveys && surveys.length > 0) {
      return surveys?.map(({ node }) => {
        return <SurveyCard surveyRef={node} key={node.id} disabled={disabled} />;
      });
    } else if (surveys && unfilteredSurveys && unfilteredSurveys.length > 0) {
      return (
        <div className={classes.fullWidth}>
          <Heading level={3}>No surveys yet</Heading>
          <Txt component="p">
            Select from any of the surveys{' '}
            <A href="#" onClick={handleShowAll}>
              here
            </A>
          </Txt>
        </div>
      );
    }
  }

  return (
    <React.Suspense fallback={<LoadingIndicator />}>
      <div className={clsx(classes.root)}>{renderSurveys()}</div>
    </React.Suspense>
  );
}
